/**
 * LSN Minerals PO Generator — matches company PO format
 * Uses jsPDF + jspdf-autotable (already installed)
 */
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
// Apply the plugin so doc.autoTable() works (jspdf-autotable v5+ requires explicit plugin apply)
applyPlugin(jsPDF);

// Convert a number to Indian words
function numberToWords(num) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convert(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  }

  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);
  let words = 'Rs. ' + (intPart > 0 ? convert(intPart) : 'Zero') + ' Rupees';
  if (decPart > 0) words += ' and ' + convert(decPart) + ' Paise';
  words += ' only';
  return words;
}

/**
 * Generate a PO PDF matching LSN Minerals format
 * @param {Object} po - PO data object
 * @param {Array}  lineItems - Array of {sl_no, part_no, description, uom, req_qty, tax_pct, unit_price, total_price}
 */
export function generatePOPdf(po) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 10;

  // ── Header box ──────────────────────────────────────────────────
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y, pageW - margin * 2, 22, 'F');

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('LSN MINERALS PRIVATE LIMITED', pageW / 2, y + 7, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('GST NO: 36AADCL0258P2Z5', pageW / 2, y + 12, { align: 'center' });
  doc.text('8-2-293/82/J/A/53, 2nd Floor, Journalist Colony, Road No.70, Jubilee Hills, Hyd – 500033, TS', pageW / 2, y + 17, { align: 'center' });
  doc.text('Contact: 9849935937, 9902151369', pageW / 2, y + 21, { align: 'center' });

  y += 26;

  // ── PO Title row ────────────────────────────────────────────────
  doc.setFillColor(30, 30, 30);
  doc.rect(margin, y, pageW - margin * 2, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('PURCHASE ORDER', pageW / 2, y + 5.5, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  y += 11;

  // ── PO meta ─────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`P.O. No.: ${po.po_number || '—'}`, margin, y + 5);
  doc.text(`Order Date: ${po.order_date || new Date().toLocaleDateString('en-IN')}`, pageW - margin, y + 5, { align: 'right' });
  y += 10;

  // ── Vendor block ─────────────────────────────────────────────────
  doc.setDrawColor(180, 180, 180);
  doc.rect(margin, y, pageW - margin * 2, 28);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('To:', margin + 2, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.text(po.vendor_name || '', margin + 2, y + 10);
  if (po.vendor_address) doc.text(po.vendor_address, margin + 2, y + 15);
  if (po.vendor_gst) doc.text(`GST: ${po.vendor_gst}`, margin + 2, y + 20);
  if (po.vendor_email) doc.text(`Email: ${po.vendor_email}`, margin + 2, y + 25);
  if (po.vendor_contact) doc.text(`Contact: ${po.vendor_contact}`, pageW - margin - 2, y + 10, { align: 'right' });
  y += 32;

  // ── Subject ──────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`Sub: Purchase Order for ${po.subject || po.part_name || ''}`, margin, y);
  y += 5;
  if (po.door_no) {
    doc.setFont('helvetica', 'normal');
    doc.text(`Equipment: ${po.door_no}`, margin, y);
    y += 5;
  }
  doc.setFont('helvetica', 'normal');
  doc.text(`Indent No.: ${po.indent_no || 'verbal'}    Ref No.: ${po.ref_no || 'Your quote'}`, margin, y);
  y += 5;
  doc.text('Dear Sir,', margin, y);
  y += 5;
  doc.text('We are pleased to place a Purchase Order to you for the supply and delivery of the items mentioned below.', margin, y, { maxWidth: pageW - margin * 2 });
  y += 8;

  // ── Line Items Table ─────────────────────────────────────────────
  const lineItems = po.line_items || [];
  doc.autoTable({
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Sl No', 'Part No', 'Description', 'UOM', 'Req Qty', 'Tax %', 'Unit Price', 'Total Price']],
    body: lineItems.map((item, idx) => [
      idx + 1,
      item.part_no || '',
      item.description || '',
      item.uom || 'NOS',
      item.req_qty || 0,
      item.tax_pct || 18,
      `₹ ${Number(item.unit_price || 0).toFixed(2)}`,
      `₹ ${Number(item.total_price || 0).toFixed(2)}`
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 22 },
      2: { cellWidth: 60 },
      3: { cellWidth: 14, halign: 'center' },
      4: { cellWidth: 15, halign: 'center' },
      5: { cellWidth: 12, halign: 'center' },
      6: { cellWidth: 24, halign: 'right' },
      7: { cellWidth: 25, halign: 'right' },
    }
  });

  y = doc.lastAutoTable.finalY + 4;

  // ── Totals block ─────────────────────────────────────────────────
  const subTotal = lineItems.reduce((s, i) => s + Number(i.total_price || 0), 0);
  const discount = Number(po.discount || 0);
  const afterDiscount = subTotal - discount;
  const gstPct = Number(po.gst_percentage || 18);
  const gstAmt = afterDiscount * gstPct / 100;
  const grandTotal = afterDiscount + gstAmt;

  const totalsX = pageW - margin - 80;
  const totalsW = 80;
  const tRow = 6;

  const rows = [
    ['Sub Total Amount:', `₹ ${subTotal.toFixed(2)}`],
    ['Special Discount:', `₹ ${discount.toFixed(2)}`],
    ['Sub Total After Discount:', `₹ ${afterDiscount.toFixed(2)}`],
    [`CGST & IGST ${gstPct}%:`, `₹ ${gstAmt.toFixed(2)}`],
    ['Grand Total:', `₹ ${grandTotal.toFixed(2)}`],
  ];

  rows.forEach(([label, value], i) => {
    if (i === rows.length - 1) {
      doc.setFillColor(240, 240, 240);
      doc.rect(totalsX, y, totalsW, tRow, 'F');
      doc.setFont('helvetica', 'bold');
    } else {
      doc.setFont('helvetica', 'normal');
    }
    doc.setFontSize(8);
    doc.text(label, totalsX + 2, y + 4);
    doc.text(value, totalsX + totalsW - 2, y + 4, { align: 'right' });
    doc.setDrawColor(200, 200, 200);
    doc.rect(totalsX, y, totalsW, tRow);
    y += tRow;
  });

  y += 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(`In Words: ${numberToWords(grandTotal)}`, margin, y);
  y += 8;

  // ── Terms ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  if (po.payment_terms) {
    doc.text(`Payment Terms: `, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(po.payment_terms, margin + 30, y);
    y += 5;
  }
  if (po.delivery) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Delivery: `, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(po.delivery, margin + 18, y);
    y += 5;
  }

  // ── Addresses ────────────────────────────────────────────────────
  const colW = (pageW - margin * 2) / 2;
  doc.setFont('helvetica', 'bold');
  doc.text('Invoicing Address:', margin, y);
  doc.text('Consignee Address:', margin + colW, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  const invLines = doc.splitTextToSize('8-2-293/82/J/A/53, 2nd Floor, Journalist Colony, Road No.70, Jubilee Hills, Hyd – 500033, TS', colW - 4);
  invLines.forEach(l => { doc.text(l, margin, y); y += 4; });
  const consLines = doc.splitTextToSize(po.consignee_address || 'Site address', colW - 4);
  let consY = y - (invLines.length * 4);
  consLines.forEach(l => { doc.text(l, margin + colW, consY); consY += 4; });
  y = Math.max(y, consY) + 4;

  // ── Closing ──────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('We request your good self to acknowledge this order and arrange for delivery at the earliest.', margin, y, { maxWidth: pageW - margin * 2 });
  y += 10;
  doc.text('Thanking you.', margin, y);
  y += 6;
  doc.text('Yours faithfully,', margin, y);
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.text('For LSN Minerals Private Limited', margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.text('Authorized Signatory', margin, y);

  // ── Download ─────────────────────────────────────────────────────
  const filename = `PO_${(po.po_number || 'draft').replace(/\//g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
